// currents.to — the session. One state machine:
//   awaken → carve → flow → bloom | spill → (draw again → carve)
// The verdict is the shared deterministic evaluation (score.ts, mirrored by
// the server); everything else here is ink, water, and sound.

import { fetchDaily, submitAttempt, type DayStats } from "./api.js";
import { AudioEngine } from "./audio.js";
import { dayNumber, EPOCH_DATE, MAX_STROKE_POINTS, MIN_STROKE_POINTS, todayUtcIso } from "./board.js";
import { dist, resample, type Point } from "./geometry.js";
import { captureGifLoop, downloadName } from "./artifact.js";
import { Flow } from "./flow.js";
import { GhostField } from "./ghosts.js";
import { generate, type Landscape } from "./landscape.js";
import {
  DARK,
  LIGHT,
  Renderer,
  type Firefly,
  type InkDrop,
  type Petal,
  type Ripple,
  type StrokeSample,
} from "./render.js";
import { evaluate, type Evaluation } from "./score.js";
import { shareString } from "./share.js";
import { Tilt } from "./tilt.js";

type State = "awaken" | "carve" | "flow" | "bloom" | "spill";

interface SessionRecord {
  v: 1;
  attempts: number;
  solved: boolean;
  flow: number;
  share: string;
  stroke: [number, number][];
}

const SESSION_PREFIX = "currents.to:session:v1:";
const META_KEY = "currents.to:meta:v1";
const GROVE_KEY = "currents.to:grove:v1";

// The grove: one entry per solved date — the player's growing blossom branch.
function loadGrove(): string[] {
  try {
    const raw = localStorage.getItem(GROVE_KEY);
    if (raw !== null) {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.every((d) => typeof d === "string")) return parsed;
    }
  } catch {
    // Fresh grove.
  }
  return [];
}

function saveGrove(grove: string[]): void {
  try {
    localStorage.setItem(GROVE_KEY, JSON.stringify(grove));
  } catch {
    // The grove just won't persist.
  }
}

// Yesterday's river, if this device carved one — an echo under today's board.
function loadEcho(todayIso: string): [number, number][] | null {
  try {
    const yesterday = new Date(Date.parse(`${todayIso}T00:00:00Z`) - 86400000).toISOString().slice(0, 10);
    const raw = localStorage.getItem(SESSION_PREFIX + yesterday);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    const record = parsed as SessionRecord;
    if (record && record.v === 1 && record.solved && Array.isArray(record.stroke) && record.stroke.length > 1) {
      return record.stroke;
    }
  } catch {
    // No echo.
  }
  return null;
}

interface MetaRecord {
  lastBloomDate: string;
  streak: number;
}

function loadMeta(): MetaRecord {
  try {
    const raw = localStorage.getItem(META_KEY);
    if (raw !== null) {
      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed === "object" && parsed !== null && typeof (parsed as MetaRecord).streak === "number") {
        return parsed as MetaRecord;
      }
    }
  } catch {
    // Fall through to a fresh record.
  }
  return { lastBloomDate: "", streak: 0 };
}

function saveMeta(meta: MetaRecord): void {
  try {
    localStorage.setItem(META_KEY, JSON.stringify(meta));
  } catch {
    // Storage unavailable: the streak just won't persist.
  }
}

// The ?date=YYYY-MM-DD archive: replay any past current. Invalid, future,
// or pre-epoch dates quietly fall back to today (the server enforces the
// same bounds).
function resolvePlayDate(): string {
  const today = todayUtcIso();
  const raw = new URLSearchParams(window.location.search).get("date");
  if (!raw || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return today;
  if (Number.isNaN(Date.parse(`${raw}T00:00:00Z`))) return today;
  if (raw > today || raw < EPOCH_DATE) return today;
  return raw;
}

function loadSession(date: string): SessionRecord {
  const empty: SessionRecord = { v: 1, attempts: 0, solved: false, flow: 0, share: "", stroke: [] };
  try {
    const raw = localStorage.getItem(SESSION_PREFIX + date);
    if (raw === null) return empty;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      (parsed as SessionRecord).v === 1 &&
      typeof (parsed as SessionRecord).attempts === "number"
    ) {
      return parsed as SessionRecord;
    }
  } catch {
    // Corrupt record: fall through to a fresh one (never silently repaired
    // into something else — just start the day again).
  }
  return empty;
}

function saveSession(date: string, record: SessionRecord): void {
  try {
    localStorage.setItem(SESSION_PREFIX + date, JSON.stringify(record));
  } catch {
    // Storage unavailable (private mode): the day just won't persist.
  }
}

function el<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`missing element #${id}`);
  return node as T;
}

class Game {
  private readonly date = resolvePlayDate();
  private readonly isToday = this.date === todayUtcIso();
  private readonly dayNo = dayNumber(this.date);
  private readonly landscape: Landscape = generate(this.date);
  private readonly renderer: Renderer;
  private readonly audio = new AudioEngine();
  private readonly tilt = new Tilt();
  private readonly ghostField = new GhostField();
  private session = loadSession(this.date);

  private state: State = "awaken";
  private revealStart = performance.now();
  private stroke: StrokeSample[] = [];
  private strokeAlpha = 1;
  private drawing = false;
  private activePointer: number | null = null;
  private lastSample: { x: number; y: number; t: number } | null = null;
  private flow: Flow | null = null;
  private flowPath: Point[] = [];
  private evaluation: Evaluation | null = null;
  private petals: Petal[] = [];
  private inkDrops: InkDrop[] = [];
  private sealAlpha = 0;
  private lastFrame = performance.now();
  private stats: DayStats | null = null;
  private serverShare: string | null = null;
  private meta = loadMeta();
  private gifBlob: Blob | null = null;
  private restoredKoiArc: number | null = null;
  private mouseLean = { x: 0, y: 0 };
  private rainStarted = false;
  private ripples: Ripple[] = [];
  private fireflies: Firefly[] = [];
  private grove = loadGrove();
  private echo: readonly [number, number][] | null = null;
  private strokeStart = 0;
  private lastUrge = 0;
  private reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  private readonly hint = el<HTMLParagraphElement>("hint");
  private readonly statsLine = el<HTMLParagraphElement>("stats");
  private readonly sharePanel = el<HTMLElement>("share-panel");
  private readonly shareText = el<HTMLPreElement>("share-text");
  private readonly breakdown = el<HTMLParagraphElement>("breakdown");
  private readonly copyButton = el<HTMLButtonElement>("copy");
  private readonly saveButton = el<HTMLButtonElement>("save-loop");
  private readonly againButton = el<HTMLButtonElement>("again");
  private readonly muteButton = el<HTMLButtonElement>("mute");

  constructor(canvas: HTMLCanvasElement) {
    const dark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    this.renderer = new Renderer(canvas, dark ? DARK : LIGHT);
    window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", (e) => {
      this.renderer.palette = e.matches ? DARK : LIGHT;
      this.renderer.resize();
    });

    const streakNote = this.meta.streak > 1 && this.isToday ? ` · streak ${this.meta.streak}` : "";
    const archiveNote = this.isToday ? "" : " · an older current";
    el<HTMLElement>("day-label").textContent = `№${this.dayNo} · ${this.prettyDate()}${streakNote}${archiveNote}`;
    this.audio.prepare(this.date, this.landscape.stones.length);

    this.renderer.resize();
    window.addEventListener("resize", () => this.renderer.resize());

    canvas.addEventListener("pointerdown", (e) => this.onPointerDown(e));
    canvas.addEventListener("pointermove", (e) => this.onPointerMove(e));
    canvas.addEventListener("pointerup", (e) => this.onPointerUp(e));
    canvas.addEventListener("pointercancel", () => this.cancelStroke());
    window.addEventListener("keydown", (e) => {
      if (e.key === "Escape") this.cancelStroke();
    });
    // Returning from the app switcher leaves the context suspended on iOS.
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) this.audio.unlock();
    });

    this.copyButton.addEventListener("click", () => void this.copyShare());
    this.saveButton.addEventListener("click", () => void this.saveArtifact());
    this.againButton.addEventListener("click", () => this.reset());
    this.muteButton.addEventListener("click", () => {
      // A click is a valid activation gesture — make sure the context exists
      // and is running before flipping the mute state.
      this.audio.start();
      const muted = this.audio.toggleMute();
      this.muteButton.textContent = muted ? "silence" : "sound";
      this.muteButton.setAttribute("aria-pressed", String(muted));
    });

    void fetchDaily(this.isToday ? undefined : this.date).then((daily) => {
      if (daily) {
        this.stats = daily.stats;
        this.renderStats();
      }
    });

    this.ghostField.start(this.date);
    this.echo = this.isToday ? loadEcho(this.date) : null;

    // Dusk sessions get fireflies — a handful of warm embers over the paper.
    const hour = new Date().getHours();
    if (!this.reducedMotion && (hour >= 19 || hour < 5)) {
      this.fireflies = Array.from({ length: 8 }, () => ({
        x: Math.random() * 1000,
        y: 200 + Math.random() * 1100,
        vx: (Math.random() - 0.5) * 18,
        vy: (Math.random() - 0.5) * 12,
        phase: Math.random() * Math.PI * 2,
      }));
    }

    if (this.reducedMotion) this.revealStart = performance.now() - 4000;

    if (this.session.solved && this.session.stroke.length >= 2) {
      // A return visit to a finished painting: show the work, signed.
      this.restoreSolved();
    } else {
      this.setHint("One stroke — from the falling drop to the waiting root.");
      window.setTimeout(() => {
        if (this.state === "awaken") this.state = "carve";
      }, this.reducedMotion ? 0 : 1400);
    }

    requestAnimationFrame((t) => this.frame(t));
  }

  private prettyDate(): string {
    return new Date(`${this.date}T00:00:00Z`).toLocaleDateString(undefined, {
      day: "numeric",
      month: "long",
      timeZone: "UTC",
    });
  }

  private setHint(text: string): void {
    this.hint.textContent = text;
  }

  private renderStats(): void {
    if (!this.stats) return;
    const { attempts, blooms } = this.stats;
    if (attempts === 0) {
      this.statsLine.textContent = "the first river of the day is yours to carve";
    } else {
      this.statsLine.textContent = `${blooms} blossom${blooms === 1 ? "" : "s"} from ${attempts} river${attempts === 1 ? "" : "s"} today`;
    }
  }

  private restoreSolved(): void {
    this.stroke = this.session.stroke.map(([x, y]) => ({ x, y, w: 12, dry: 0.2 }));
    this.flowPath = this.session.stroke.map(([x, y]) => [x, y] as const);
    this.evaluation = evaluate(this.landscape, this.flowPath);
    this.flow = null;
    this.state = "bloom";
    this.sealAlpha = 1;
    this.serverShare = this.session.share || null;
    if (this.evaluation.koiJoined) this.restoredKoiArc = 0; // the koi rests at the root
    this.showSharePanel();
    this.setHint(`flow ${this.session.flow} · the blossom opened`);
    void this.captureArtifact();
  }

  // --- carving -------------------------------------------------------------

  private onPointerDown(e: PointerEvent): void {
    this.audio.start();
    if (this.landscape.weather === "rain" && !this.rainStarted) {
      this.rainStarted = true;
      this.audio.rainOn();
    }
    void this.tilt.enable();
    if (this.state === "flow") {
      // Urge the water: a touch near the current gives it a moment of haste
      // (theatre only — the verdict was sealed at release).
      const [bx, by] = this.renderer.toBoard(e.clientX, e.clientY);
      const now = performance.now();
      if (this.flow && now - this.lastUrge > 350) {
        const head = this.flow.pointAtArc(this.flow.head);
        if (dist(bx, by, head[0], head[1]) < 160) {
          this.lastUrge = now;
          this.flow.urge();
          this.ripples.push({ x: bx, y: by, born: now });
          this.audio.plip();
          this.vibrate(6);
        }
      }
      return;
    }
    if (this.state === "bloom") {
      // The day is solved: tapping the painting lifts the slip of paper away
      // so the work can be seen, and lays it back down again.
      if (this.session.solved) this.sharePanel.hidden = !this.sharePanel.hidden;
      return;
    }
    if (this.state !== "carve" && this.state !== "spill") return;
    if (this.drawing) return; // one brush; a second finger cannot join a stroke
    if (this.state === "spill") this.reset();
    this.drawing = true;
    this.activePointer = e.pointerId;
    this.strokeStart = performance.now();
    this.stroke = [];
    this.strokeAlpha = 1;
    try {
      this.renderer.canvas.setPointerCapture(e.pointerId);
    } catch {
      // Synthetic events (tests) have no active pointer to capture.
    }
    const [x, y] = this.renderer.toBoard(e.clientX, e.clientY);
    this.stroke.push({ x, y, w: 22, dry: 0 });
    this.lastSample = { x, y, t: performance.now() };
  }

  private onPointerMove(e: PointerEvent): void {
    if (this.state === "flow") {
      // Desktop lean: no gyroscope, so the pointer's position leans the
      // current instead — the tilt mechanic, translated.
      const rect = this.renderer.canvas.getBoundingClientRect();
      this.mouseLean.x = Math.max(-1, Math.min(1, ((e.clientX - rect.left) / rect.width - 0.5) * 2.4));
      this.mouseLean.y = Math.max(-1, Math.min(1, ((e.clientY - rect.top) / rect.height - 0.5) * 2.4));
      return;
    }
    if (!this.drawing || !this.lastSample) return;
    if (e.pointerId !== this.activePointer) return;
    const [x, y] = this.renderer.toBoard(e.clientX, e.clientY);
    const now = performance.now();
    const d = dist(this.lastSample.x, this.lastSample.y, x, y);
    if (d < 5) return;
    const dt = Math.max(1, now - this.lastSample.t);
    const speed = Math.min(1, d / dt / 1.4); // 0 slow … 1 fast
    const width = 24 - speed * 17;
    this.stroke.push({ x, y, w: width, dry: speed });
    this.lastSample = { x, y, t: now };
    this.audio.brush(speed);
    if (this.stroke.length >= MAX_STROKE_POINTS) this.release();
  }

  private onPointerUp(e: PointerEvent): void {
    // pointerup is the touchend-equivalent iOS accepts as audio activation.
    this.audio.unlock();
    if (this.drawing && e.pointerId === this.activePointer) this.release();
  }

  private cancelStroke(): void {
    if (!this.drawing) return;
    this.drawing = false;
    this.activePointer = null;
    this.stroke = [];
    this.lastSample = null;
  }

  // --- release & flow ------------------------------------------------------

  private release(): void {
    this.drawing = false;
    this.activePointer = null;
    this.lastSample = null;
    if (this.stroke.length < MIN_STROKE_POINTS) {
      // A tap, not a stroke. Tapping a stone listens to it — the stone
      // ripples and whispers the chime it will sing when the water passes.
      const tapped = this.stroke[0];
      const quick = performance.now() - this.strokeStart < 400;
      this.stroke = [];
      if (tapped && quick) {
        const ordered = [...this.landscape.stones].sort((a, b) => a.y - b.y);
        for (let i = 0; i < ordered.length; i++) {
          const stone = ordered[i];
          if (dist(tapped.x, tapped.y, stone.x, stone.y) < stone.r + 14) {
            this.ripples.push({ x: stone.x, y: stone.y, born: performance.now() });
            this.audio.listen(i);
            this.vibrate(5);
            return;
          }
        }
      }
      this.setHint("The brush barely touched the paper. One full stroke, drop to root.");
      return;
    }

    let points: Point[] = this.stroke.map((s) => [s.x, s.y] as const);
    if (points.length > MAX_STROKE_POINTS) points = resample(points, MAX_STROKE_POINTS);

    this.session.attempts += 1;
    const attempt = this.session.attempts;
    this.evaluation = evaluate(this.landscape, points);
    this.flowPath = points;
    this.state = "flow";
    this.setHint("");
    this.audio.ambientOn();

    this.flow = new Flow(points, this.landscape, this.evaluation, {
      onStone: (i, graced) => {
        this.audio.chime(i, graced);
        this.vibrate(graced ? 12 : 6);
      },
      onGap: () => this.audio.swoosh(),
      onArrive: () => this.onBloom(attempt),
      onBreak: (at) => this.onSpill(at, attempt),
      onKoi: () => {
        this.audio.koi();
        this.vibrate([10, 30, 10]);
      },
      onReed: () => this.audio.reeds(),
      onEddy: () => {
        this.audio.eddy();
        this.vibrate(24);
      },
      onFalls: (at) => {
        this.audio.falls();
        this.vibrate([12, 40, 20]);
        const now = performance.now();
        const count = this.reducedMotion ? 4 : 12;
        for (let i = 0; i < count; i++) {
          const angle = Math.random() * Math.PI - Math.PI; // upward fan
          const speed = 30 + Math.random() * 90;
          this.inkDrops.push({
            x: at[0],
            y: at[1] + 10,
            vx: Math.cos(angle) * speed * 0.5,
            vy: Math.sin(angle) * speed,
            size: 1.8 + Math.random() * 3,
            born: now,
            tone: "water",
          });
        }
      },
    });

    // The server is the authority for the shared record; the local verdict
    // is identical by construction (same math), so the theatre never waits.
    void submitAttempt(this.date, points, attempt).then((res) => {
      if (res) {
        this.serverShare = res.share;
        this.stats = res.stats;
        this.renderStats();
      }
    });

    saveSession(this.date, this.session);
  }

  private vibrate(pattern: number | number[]): void {
    if (this.reducedMotion) return;
    try {
      navigator.vibrate?.(pattern);
    } catch {
      // No haptics here; the chimes carry it.
    }
  }

  private updateStreak(): void {
    if (!this.isToday || this.meta.lastBloomDate === this.date) return;
    const yesterday = new Date(Date.parse(`${this.date}T00:00:00Z`) - 86400000).toISOString().slice(0, 10);
    this.meta = {
      lastBloomDate: this.date,
      streak: this.meta.lastBloomDate === yesterday ? this.meta.streak + 1 : 1,
    };
    saveMeta(this.meta);
  }

  private onBloom(attempt: number): void {
    this.state = "bloom";
    this.audio.bloom();
    this.vibrate([18, 50, 18, 50, 30]);
    const now = performance.now();
    const { x, y } = this.landscape.root;
    const count = this.reducedMotion ? 8 : 26;
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 12 + Math.random() * 46;
      this.petals.push({
        x,
        y: y + 6,
        vx: Math.cos(angle) * speed,
        vy: -30 - Math.random() * 50,
        rot: Math.random() * Math.PI * 2,
        vrot: (Math.random() - 0.5) * 2.4,
        size: 6 + Math.random() * 7,
        born: now,
      });
    }
    window.setTimeout(() => {
      this.sealAlpha = 0.001; // begins the stamp animation in frame()
    }, this.reducedMotion ? 0 : 700);

    const flow = this.evaluation?.flow ?? 0;
    const previousBest = this.session.solved ? this.session.flow : -1;
    if (flow > previousBest) {
      // First bloom of the day, or a finer river while refining.
      this.session.flow = flow;
      this.session.stroke = this.flowPath.map((p) => [p[0], p[1]]);
      this.session.share = this.localShare(attempt);
      this.serverShare = null; // the pending submit response will refresh it
    }
    this.session.solved = true;
    saveSession(this.date, this.session);
    this.updateStreak();
    if (!this.grove.includes(this.date)) {
      this.grove.push(this.date);
      saveGrove(this.grove);
    }

    if (previousBest === -1) {
      this.setHint(`flow ${flow} · the blossom opens`);
    } else if (flow > previousBest) {
      this.setHint(`flow ${flow} · your finest current yet`);
    } else {
      this.setHint(`flow ${flow} · the river remembers ${previousBest}`);
    }
    void this.autoCopyShare();
    window.setTimeout(() => {
      this.showSharePanel();
      this.audio.ambientOff();
      void this.captureArtifact();
    }, this.reducedMotion ? 300 : 1600);
  }

  // Spec: solving the day copies the cryptic share to the clipboard by
  // itself. Browsers may refuse outside a gesture — the copy button remains.
  private async autoCopyShare(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.currentShare());
      this.copyButton.textContent = "copied to clipboard";
      window.setTimeout(() => (this.copyButton.textContent = "copy the current"), 2600);
    } catch {
      // The button is the fallback.
    }
  }

  // Spec: the app creates the looping artifact automatically. A silent
  // replay runs the water again while frames are sampled into a GIF.
  private async captureArtifact(): Promise<void> {
    if (this.gifBlob || !this.flowPath.length || !this.evaluation) return;
    if (this.reducedMotion) return; // no surprise replay under reduced motion
    const replay = new Flow(this.flowPath, this.landscape, this.evaluation, {
      onStone: () => undefined,
      onGap: () => undefined,
      onArrive: () => undefined,
      onBreak: () => undefined,
    });
    this.flow = replay;
    this.saveButton.disabled = true;
    this.saveButton.textContent = "the loop is brewing…";
    this.gifBlob = await captureGifLoop(this.renderer.canvas);
    this.saveButton.disabled = false;
    this.saveButton.hidden = this.gifBlob === null;
    this.saveButton.textContent = "save the loop";
  }

  private onSpill(at: Point, attempt: number): void {
    this.state = "spill";
    this.audio.spill();
    this.vibrate(50);
    this.audio.ambientOff();
    const now = performance.now();
    const count = this.reducedMotion ? 5 : 14;
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 18 + Math.random() * 60;
      this.inkDrops.push({
        x: at[0],
        y: at[1],
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed * 0.6,
        size: 2.5 + Math.random() * 5,
        born: now,
      });
    }
    this.strokeAlpha = 0.42;
    const reason = this.evaluation?.reason ?? "the water is lost";
    this.setHint(`${reason} — the ink dries. Draw again.`);
    saveSession(this.date, this.session);
    void attempt;
  }

  private reset(): void {
    this.state = "carve";
    this.stroke = [];
    this.strokeAlpha = 1;
    this.flow = null;
    this.flowPath = [];
    this.evaluation = null;
    this.inkDrops = [];
    this.restoredKoiArc = null;
    this.setHint(
      this.session.solved
        ? `the river remembers ${this.session.flow} — carve a finer one`
        : "One stroke — from the falling drop to the waiting root.",
    );
    this.sharePanel.hidden = true;
  }

  // --- share & artifact ----------------------------------------------------

  private localShare(attempt: number): string {
    if (!this.evaluation) return "";
    return shareString(this.dayNo, this.evaluation, attempt);
  }

  private currentShare(): string {
    const base = this.serverShare ?? this.session.share ?? this.localShare(this.session.attempts);
    if (this.isToday && this.meta.streak > 1) {
      const lines = base.split("\n");
      if (lines.length === 3) {
        lines[2] += ` · streak ${this.meta.streak}`;
        return lines.join("\n");
      }
    }
    return base;
  }

  private renderBreakdown(): void {
    const c = this.evaluation?.components ?? null;
    if (!c) {
      this.breakdown.textContent = "";
      return;
    }
    let text = `grace ${c.grace} · economy ${c.economy} · daring ${c.daring}`;
    if (this.evaluation?.koiJoined) text += " · koi +5";
    if (this.evaluation?.fallsLeapt) text += " · falls +3";
    if (this.evaluation?.eddyCrossed) text += " · eddy −8";
    this.breakdown.textContent = text;
  }

  private showSharePanel(): void {
    this.shareText.textContent = this.currentShare();
    this.renderBreakdown();
    this.sharePanel.hidden = false;
    this.saveButton.hidden = this.gifBlob === null && this.reducedMotion;
    // The day is complete, but the river can always be refined.
    this.againButton.hidden = false;
    this.againButton.textContent = "refine the river";
  }

  private async copyShare(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.currentShare());
      this.copyButton.textContent = "copied";
      window.setTimeout(() => (this.copyButton.textContent = "copy the current"), 1600);
    } catch {
      this.copyButton.textContent = "select & copy above";
    }
  }

  private async saveArtifact(): Promise<void> {
    if (!this.gifBlob) {
      await this.captureArtifact();
      if (!this.gifBlob) {
        this.saveButton.textContent = "the loop escaped — try again";
        return;
      }
    }
    const url = URL.createObjectURL(this.gifBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = downloadName(this.dayNo);
    a.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 30000);
  }

  // --- frame loop ----------------------------------------------------------

  private frame(now: number): void {
    const dt = Math.min(0.05, (now - this.lastFrame) / 1000);
    this.lastFrame = now;

    const reveal = this.reducedMotion ? 1 : Math.min(1, (now - this.revealStart) / 2400);

    if (this.flow && (this.state === "flow" || this.state === "bloom")) {
      const headPoint = this.flow.pointAtArc(this.flow.head);
      const direction = this.flow.directionAtArc(this.flow.head);
      // Gyroscope where there is one; pointer lean where there isn't.
      const sensor = this.tilt.read();
      const lean = {
        x: Math.max(-1, Math.min(1, sensor.x + this.mouseLean.x * 0.6)),
        y: Math.max(-1, Math.min(1, sensor.y + this.mouseLean.y * 0.6)),
      };
      this.flow.step(dt, lean, headPoint, direction);
    }

    if (this.sealAlpha > 0 && this.sealAlpha < 1) {
      this.sealAlpha = Math.min(1, this.sealAlpha + dt * 3.2);
    }

    for (const petal of this.petals) {
      petal.x += (petal.vx + Math.sin(now / 700 + petal.born) * 14) * dt;
      petal.y += petal.vy * dt;
      petal.vy += 26 * dt; // drift down after the burst
      petal.rot += petal.vrot * dt;
    }
    this.petals = this.petals.filter((p) => (now - p.born) / 1000 < 6);
    for (const drop of this.inkDrops) {
      drop.x += drop.vx * dt;
      drop.y += drop.vy * dt;
      drop.vx *= 0.92;
      drop.vy *= 0.92;
    }
    this.inkDrops = this.inkDrops.filter((d) => (now - d.born) / 1000 < 1.2);
    this.ripples = this.ripples.filter((r) => (now - r.born) / 1000 < 1.2);
    for (const fly of this.fireflies) {
      fly.x += fly.vx * dt;
      fly.y += fly.vy * dt;
      fly.vx += (Math.random() - 0.5) * 6 * dt;
      fly.vy += (Math.random() - 0.5) * 6 * dt;
      if (fly.x < 20 || fly.x > 980) fly.vx *= -1;
      if (fly.y < 120 || fly.y > 1380) fly.vy *= -1;
    }

    const ghosts = this.state === "carve" || this.state === "awaken" ? this.ghostField.visible(now) : [];

    this.renderer.frame({
      landscape: this.landscape,
      reveal,
      now,
      stroke: this.stroke,
      strokeAlpha: this.strokeAlpha,
      ghosts,
      waterPath: this.flowPath.length > 1 ? this.flowPath : null,
      waterHead: this.flow ? this.flow.head : this.state === "bloom" ? Number.POSITIVE_INFINITY : 0,
      petals: this.petals,
      inkDrops: this.inkDrops,
      sealAlpha: this.sealAlpha,
      dayNo: this.dayNo,
      koiSwimArc: this.flow?.koiSwimArc ?? this.restoredKoiArc,
      ripples: this.ripples,
      fireflies: this.fireflies,
      groveCount: this.grove.length,
      echo: this.echo,
    });

    requestAnimationFrame((t) => this.frame(t));
  }
}

const canvas = document.getElementById("board");
if (canvas instanceof HTMLCanvasElement) {
  new Game(canvas);
}
